'use client';

import dynamic from 'next/dynamic';
import DefaultAppNavBar from '../navBar/DefaultAppNavBar';
import { ListingPriceHeader } from './ListingPriceHeader';
import ListingAddress from '../listingDetails/ListingAddress';
import ListingStatusSection from '../listingDetails/ListingStatusSection';
import ListingSpaceSummary from '../listingDetails/ListingSpaceSummary';
import HorizontalLine from '../HorizontalLine';
import ListingOfferDetails from '../listingDetails/ListingOfferDetails';
import ListingOpenHouseSection from '../listingDetails/ListingOpenHouseSection';
import WatchedNeighborhoodCard from '../listingDetails/WatchedNeighborhoodCard';
import ListingHistory from '../listingDetails/ListingHistory';
import ListingKeyDetails from '../listingDetails/ListingKeyDetails';
import ListingFullDetails from '../listingDetails/ListingFullDetails';
import ListingEstimateSection from '../listingDetails/ListingEstimateSection';
import SimilarSolds from '../listingDetails/SimilarSolds';
import AgentProfile from '../listingDetails/AgentProfile';
import DefaultBrokerageFooter from '../listingDetails/DefaultBrokerageFooter';
import {
  FullListing,
  FullListingObject,
} from '@th/shared/src/types/FullListing';
import { Agent, AgentObject } from '@th/shared/src/types/Agent';
import { SimpleListingObject } from '@th/shared/src/types/SimpleListing';
import isListingBlurred from '../../helpers/isListingBlurred';
import { useUser } from '../../contexts/UserContext';
import ImageHero from './ImageHero';
import ListingBuildingPermits from './ListingBuildingPermits';
import PropertyInquiryMobileStickyButton from '../PropertyInquiryMobileStickyButton';
import PropertyInquirySection from './PropertyInquirySection';
import useSaveUtmParamsToLocalStorageAndCreateInitialAnalyticsEvent from '../../hooks/useSaveUtmParamsToLocalStorageAndCreateInitialAnalyticsEvent';
import StaticMapWithButton from '../map/StaticMapWithButton';
import { featureFlags } from '../../utils/featureFlags';

const DynamicOpenInAppBanner = dynamic(
  () => import('../../components/OpenInAppBanner'),
  { ssr: false },
);

type Props = {
  listingObject: FullListingObject;
  agentObject: AgentObject;
  // listingBranchLink: string;
  historicalListingObjects: Array<SimpleListingObject>;
};

export default function ListingPage({
  listingObject,
  agentObject,
  // listingBranchLink,
  historicalListingObjects,
}: Props) {
  const { isLoggedIn, user } = useUser();
  const listing = FullListing.fromObject(listingObject);
  const agent = agentObject ? Agent.fromObject(agentObject) : null;
  const isBlurred = isListingBlurred(listing, isLoggedIn);

  // NOTE: We're hiding cashback content for now, but we may decide to bring it back for some web users in the future
  // const showCashbackInfo = listing.isSale() && !user?.isVerifiedAgent();

  // TODO: remove this once we figure out how to log analytics events for all pages on the app-router layout level
  useSaveUtmParamsToLocalStorageAndCreateInitialAnalyticsEvent();

  // TODO: Remove this. This log is useful to get the zip of the listing while testing clustering logic. Zip is not really useful for end user though
  console.log(listing.address.formattedFullMailingAddress());

  return (
    <div className="max-w-screen-xl relative mx-auto sm:px-6 lg:px-8">
      {/*<DynamicOpenInAppBanner branchLink={listingBranchLink} />*/}

      <DefaultAppNavBar />

      <ImageHero listing={listing} isBlurred={isBlurred} />

      {/* Main Body */}
      <div className="flex flex-col sm:flex-row justify-center">
        {/* Listing Details */}
        <div className="max-w-4xl " style={{ flex: 2 }}>
          <div className="pl-4 sm:pl-0 pr-4 py-4">
            <ListingPriceHeader listing={listing} isBlurred={isBlurred} />
            <ListingAddress listing={listing} isBlurred={isBlurred} />
            <ListingStatusSection listing={listing} isBlurred={isBlurred} />
            <div className="mb-2 md:mb-6">
              <p className={'text-sm text-secondaryText'}>
                {listing.propertyTypeText()}
              </p>
            </div>
            <div className="mb-4 md:mb-12">
              <ListingSpaceSummary
                listing={listing}
                isBlurred={isBlurred}
                className={'space-x-8'}
              />
            </div>
            {/*{showCashbackInfo && <ListingCashbackCtaCard />}*/}
            <HorizontalLine />
            {featureFlags.SHOW_OFFER_DATES && (
              <div className="mb-4">
                <ListingOfferDetails
                  listing={listing}
                  bottomDivider={<HorizontalLine />}
                />
              </div>
            )}
            <div className="mb-4">
              <ListingOpenHouseSection
                listing={listing}
                bottomDivider={<HorizontalLine />}
              />
            </div>
            <div className="relative mb-4 md:mb-12">
              <StaticMapWithButton coordinates={listing.coordinates} />
            </div>
            <div className="block sm:hidden">
              {listing.address.neighborhood && (
                <WatchedNeighborhoodCard listing={listing} className={'mb-6'} />
              )}
            </div>
            <div className="mb-4">
              <ListingHistory
                historicalListingObjects={historicalListingObjects}
                listingId={listing.id}
              />
            </div>
            <div className="mb-4">
              <ListingBuildingPermits listingId={listing.id} />
            </div>
            <div className="mb-4">
              <ListingKeyDetails listing={listing} isBlurred={isBlurred} />
            </div>
            <HorizontalLine />
            <div className="mb-4">
              <h3 className="mb-4">Description</h3>
              <p>{listing.details.description}</p>
            </div>
            <HorizontalLine />
            <div className="mb-4">
              <h3 className="mb-4">Details</h3>
              <ListingFullDetails listing={listing} isBlurred={isBlurred} />
            </div>
            <HorizontalLine />
            <>
              <div className="mb-4">
                <ListingEstimateSection listingId={listing.id} />
              </div>
              <HorizontalLine />
            </>
          </div>
        </div>

        {/*{listing.isSale() ? (*/}
        {/*  <CashbackInquirySection listing={listing} isBlurred={isBlurred} />*/}
        {/*) : (*/}
        <PropertyInquirySection
          listing={listing}
          isBlurred={isBlurred}
          agent={agent}
        />
        {/*)}*/}
      </div>

      <div className="mb-4 ">
        <SimilarSolds listingId={listing.id} />
      </div>

      {/*Footer*/}
      <div className="py-4">
        <HorizontalLine />
        {agent && (
          <div className="mb-4 md:mb-12">
            <AgentProfile agent={agent} />
          </div>
        )}
        <div>
          <h3>Listing Brokerage</h3>
          <p>{listing.brokerageName}</p>
        </div>
        {!agent && (
          <>
            <HorizontalLine />
            <DefaultBrokerageFooter listing={listing} />
          </>
        )}
        {/*<AgentAppDownloadButton*/}
        {/*  agent={agent}*/}
        {/*  listingBranchLink={listingBranchLink}*/}
        {/*/>*/}
      </div>

      {/*{listing.isSale() ? (*/}
      {/*  <CashbackInquiryMobileStickyButton*/}
      {/*    listing={listing}*/}
      {/*    isBlurred={isBlurred}*/}
      {/*  />*/}
      {/*) : (*/}
      <PropertyInquiryMobileStickyButton
        listing={listing}
        agent={agent}
        isBlurred={isBlurred}
      />
      {/*)}*/}
    </div>
  );
}
